/**
 * Llogaritja e pagës në të dy drejtimet.
 *
 * `netFromGross` është formula E VETME e pagës: `AddList` e thërret këtë edhe
 * kur ndërton rreshtat, edhe kur rillogarit gjatë shkrimit. Më parë e njëjta
 * formulë ishte shkruar tri herë (këtu, te `prepareEmployeeCard` dhe te
 * `computeFiscalTotals`) — dhe të treja e kishin të njëjtin gabim, sepse ishin
 * kopje të njëra-tjetrës.
 *
 * ── Kontributi llogaritet mbi BRUTON, jo mbi pagën bazë ──────────────────────
 * Ligji Nr. 04/L-101 e llogarit kontributin pensional (5% + 5%) mbi pagën bruto,
 * pra bashkë me orët shtesë dhe bonusin; kalkulatori i ATK-së ka një fushë të
 * vetme «BRUTO». Këtu zbritej vetëm mbi pagën bazë: me orë shtesë, Trusti merrte
 * më pak nga të dy anët dhe baza e tatueshme dilte më e lartë se ç'duhej — pra
 * punëtori paguante tatim mbi një shumë mbi të cilën i takonte kontributi.
 *
 * `grossFromNet` e zgjidh të kundërtën me kërkim binar, jo me algjebër. Arsyeja:
 * neto varet nga paga bazë përmes brezave tatimorë — një funksion pjesë-pjesë
 * linear që ndryshon me çdo politikë fiskale. Një formulë e nxjerrë me dorë do
 * të vlente vetëm për brezat e sotëm, dhe do të prishej në heshtje ditën që
 * dikush shton një brez të pestë. Kërkimi binar nuk e njeh formën e funksionit;
 * i mjafton që neto rritet me pagën, çka është gjithnjë e vërtetë sa kohë normat
 * janë nën 100%.
 */

const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

/**
 * Tatimi progresiv mbi brezat — çdo brez tatohet vetëm mbi pjesën brenda tij.
 *
 * Emrat e fushave lexohen në të katër format sepse brezat vijnë nga tri burime:
 * API-ja (`From/To/Tax`), tabela e bazës (`MinAmount/MaxAmount/**Percent**`) dhe
 * konstantet e kodit (`from/to/tax`). `Percent` mungonte këtu — dhe një brez i
 * lexuar pa normë nuk jep gabim, jep **tatim zero**: e vetmja shenjë do të ishte
 * një listë page ku askush nuk tatohet.
 */
export function taxOn(amount, ranges) {
  const base = Math.max(0, num(amount));
  let total = 0;
  (ranges || []).forEach((r) => {
    const from = num(r.From ?? r.from ?? r.MinAmount ?? r.min_amount);
    const toRaw = r.To ?? r.to ?? r.MaxAmount ?? r.max_amount;
    const to = (toRaw === null || toRaw === undefined || toRaw === '' || num(toRaw) <= 0) ? Infinity : num(toRaw);
    const rate = num(r.Tax ?? r.tax ?? r.Percent ?? r.percent);
    const slice = Math.max(0, Math.min(base, to) - from);
    if (slice > 0) total += slice * (rate / 100);
  });
  return round2(total);
}

/**
 * Bruto → neto, me zbërthimin e plotë.
 *
 * @param {number} salary — paga bazë (kontributet dhe sigurimi llogariten mbi të)
 * @param {object} p — orët shtesë, bonusi, ndalesat, normat dhe brezat
 */
export function netFromGross(salary, p = {}) {
  const base       = Math.max(0, num(salary));
  const overtime   = num(p.overtimePay);
  const bonus      = num(p.bonus);
  const advances   = num(p.advances);
  const penalties  = num(p.penalties);
  const extraHealth = num(p.personalHealthIns) + num(p.familyHealthIns);
  const pctEmp     = num(p.pensionEmployee ?? 5);
  const pctEmr     = num(p.pensionEmployer ?? 5);
  // Sigurimi shëndetësor: parazgjedhja është ZERO, jo 3.5%. Ligji për sigurimin
  // e detyrueshëm shëndetësor nuk është funksionalizuar — asnjë kontribut i tillë
  // nuk mbahet sot, dhe kalkulatori i ATK-së nuk e ka fare këtë rresht. Kur të
  // hyjë në zbatim, norma vendoset te politika fiskale dhe vjen prej andej.
  const pctHealth  = num(p.healthInsEmployee ?? 0);

  const gross           = round2(base + overtime + bonus);
  const contribEmployee = round2((pctEmp / 100) * gross);
  const contribEmployer = round2((pctEmr / 100) * gross);
  const health = round2((pctHealth / 100) * gross);
  const taxable         = Math.max(0, round2(gross - contribEmployee));
  // Punësim sekondar: e gjithë baza me normën e sipërme, pa brez të liruar —
  // i njëjti rregull si te regjistri dhe te llogaritja e serverit.
  const topRate         = Math.max(0, ...(p.ranges || []).map((r) => num(r.Tax ?? r.tax ?? r.Percent ?? r.percent)));
  const tax             = p.secondJob ? round2(taxable * (topRate / 100)) : taxOn(taxable, p.ranges);
  const net = round2(taxable - tax - advances - penalties - health - extraHealth);

  return {
    base: round2(base), overtime: round2(overtime), bonus: round2(bonus),
    gross, contribEmployee, contribEmployer, health, extraHealth,
    taxable, tax, advances: round2(advances), penalties: round2(penalties),
    net,
    // Sa i kushton punëdhënësit gjithsej ky rresht.
    employerCost: round2(gross + contribEmployer),
  };
}

/**
 * Neto → bruto (gross-up).
 *
 * Kërkim binar mbi pagën bazë derisa neto të përputhet me qindarkën. Kufiri i
 * sipërm nis nga një shumëfish i netos dhe dyfishohet nëse s'mjafton — një kufi
 * fiks do të dështonte te normat e larta ose te ndalesa të mëdha.
 *
 * @returns {object|null} i njëjti zbërthim si netFromGross, ose null kur neto e
 *   kërkuar është e paarritshme (p.sh. ndalesat e tejkalojnë çdo pagë).
 */
export function grossFromNet(targetNet, p = {}, { tolerance = 0.005, maxIter = 200 } = {}) {
  const target = num(targetNet);
  if (target <= 0) return null;

  let hi = Math.max(target, 100);
  let guard = 0;
  while (netFromGross(hi, p).net < target) {
    hi *= 2;
    if (++guard > 60) return null;         // e paarritshme
  }

  let lo = 0;
  let best = null;
  for (let i = 0; i < maxIter; i++) {
    const mid = (lo + hi) / 2;
    const r = netFromGross(mid, p);
    best = r;
    const diff = r.net - target;
    if (Math.abs(diff) <= tolerance) break;
    if (diff > 0) hi = mid; else lo = mid;
  }
  if (!best) return null;

  // Rrumbullakimi te qindarka e prish përputhjen me disa cent; rillogaritet mbi
  // pagën e rrumbullakosur që rreshti i ruajtur të jetë ai që u tregua.
  return netFromGross(round2(best.base), p);
}


/** Orët standarde mujore sipas LP Neni 20: 40 orë/javë × 52/12. */
export const MONTHLY_HOURS = 173.33;

/**
 * Pagesa e orëve shtesë — një formulë për ekranin, dialogun dhe pagën.
 *
 * Dy familje, sepse paga mujore i mbulon tashmë orët e planifikuara:
 *
 *   orë të plota  — jashtë orarit, ditë pushimi, festë në ditë pushimi:
 *                   ora NUK është në pagë → orë e plotë + shtesa (p.sh. 150%);
 *   vetëm shtesa  — natë, kujdestari, festë në ditë PUNE: ora është në pagë →
 *                   shtohet vetëm përqindja (p.sh. 50%).
 *
 * Pa këtë ndarje, një festë e punuar në ditë pune paguhej 250%: një herë brenda
 * pagës mujore dhe një herë e gjysmë si shtesë.
 */
export function extraHoursPay(row = {}, baseSalary = 0, rates = {}) {
  const hourly = num(baseSalary) / MONTHLY_HOURS;
  const pct = (v, d) => {
    if (v === 0 || v === '0') return 0;
    const n = num(v);
    return n || d;
  };
  const rOvertime = pct(rates.OvertimeRate,  30);
  const rWeekend  = pct(rates.WeekendRate,   50);
  const rHoliday  = pct(rates.HolidayRate,   50);
  const rNight    = pct(rates.NightWorkRate, 30);
  const rOnCall   = pct(rates.OnCallRate,    20);

  // Çmimi për orë rrumbullakoset TE QINDARKA, dhe pagesa del prej tij — jo nga
  // një llogaritje e dytë me shumë dhjetore. Përndryshe shuma e rreshtave te
  // dialogu nuk do t'i binte totalit, dhe fletëpagesa do të printonte një çmim
  // që nuk e jep shumën e vet.
  const hourRates = {
    overtime:      round2(hourly * (1 + rOvertime / 100)),
    rest:          round2(hourly * (1 + rWeekend  / 100)),
    holiday:       round2(hourly * (1 + rHoliday  / 100)),
    night:         round2(hourly * (rNight   / 100)),
    holidayWorked: round2(hourly * (rHoliday / 100)),
    onCall:        round2(hourly * (rOnCall  / 100)),
  };

  const parts = {
    overtime:      num(row.ExtraHours)              * hourRates.overtime,
    rest:          num(row.ExtraHoursWeekend)       * hourRates.rest,
    holiday:       num(row.ExtraHoursHoliday)       * hourRates.holiday,
    night:         num(row.ExtraHoursNight)         * hourRates.night,
    holidayWorked: num(row.ExtraHoursHolidayWorked) * hourRates.holidayWorked,
    onCall:        num(row.ExtraHoursOnCall)        * hourRates.onCall,
  };
  const total = Object.values(parts).reduce((a, b) => a + b, 0);
  return {
    hourly: round2(hourly),
    rates: { rOvertime, rWeekend, rHoliday, rNight, rOnCall },
    hourRates,
    parts,
    total: round2(total),
  };
}

/** Parametrat nga një rresht page + politika fiskale e periudhës.
 *
 * Normat vijnë GJITHMONË nga politika, edhe për një rresht të ruajtur më parë:
 * përndryshe një pagë e llogaritur dikur me sigurim 3.5% do ta mbante atë
 * zbritje përgjithmonë, edhe pasi norma të bëhet zero.
 */
export function paramsFromRow(row = {}, policy = {}, ranges = []) {
  const pct = (v, d) => {
    if (v === 0 || v === '0') return 0;          // zero është normë, jo mungesë
    const n = num(v);
    if (!n) return d;
    return n >= 1 && n <= 100 ? n : n * 100;     // pranon 0.05 dhe 5 (1 = 1%, jo 100%)
  };
  return {
    overtimePay: num(row.HourSalary),
    bonus:       num(row.Bonus),
    advances:    num(row.Advances),
    penalties:   num(row.Penalties),
    personalHealthIns: num(row.HealthInsPersonal),
    familyHealthIns:   num(row.HealthInsFamily),
    pensionEmployee: num(row.EmployeeTax) > 0 ? num(row.EmployeeTax) : pct(policy.PensionEmployee, 5),
    pensionEmployer: num(row.EmployerTax) > 0 ? num(row.EmployerTax) : pct(policy.PensionEmployer, 5),
    healthInsEmployee: pct(policy.HealthInsEmployee, 0),
    secondJob: (row.SecondJob ?? row.second_job) === true,
    ranges,
  };
}

/**
 * Paga e kontratës — fushat që varen nga njëra-tjetra, sipas ligjit.
 *
 *   ora      = paga bazë ÷ 173.33           (LP Neni 20: 40 orë/javë × 52/12)
 *   fundjava = ora × (1 + shtesa e fundjavës)  (parazgjedhje +50%)
 *   festa    = ora × (1 + shtesa e festës)     (parazgjedhje +50%)
 *
 * Shtesat vijnë nga politika fiskale e periudhës (fiscalPoliciesExt), që
 * kompania mund t'i rregullojë mbi minimumin ligjor. Kontrata ruan çmimet e
 * plota për orë; pagesa e orëve shtesë te lista e pagave e ndan vetë rastin
 * "vetëm shtesa" (extraHoursPay).
 */
const ratePct = (v, d) => {
  if (v === 0 || v === '0') return 0;
  const n = num(v);
  return n || d;
};

export function contractPayFromBase(base, rates = {}) {
  const b = num(base);
  if (!b) return { BaseSalary: '', HourSalary: '', HourSalaryWeekend: '', HourSalaryHoliday: '' };
  const hour = round2(b / MONTHLY_HOURS);
  return {
    BaseSalary: round2(b),
    HourSalary: hour,
    HourSalaryWeekend: round2(hour * (1 + ratePct(rates.WeekendRate, 50) / 100)),
    HourSalaryHoliday: round2(hour * (1 + ratePct(rates.HolidayRate, 50) / 100)),
  };
}

export function contractPayFromHourly(hourly, rates = {}) {
  const h = num(hourly);
  if (!h) return { BaseSalary: '', HourSalary: '', HourSalaryWeekend: '', HourSalaryHoliday: '' };
  return {
    BaseSalary: round2(h * MONTHLY_HOURS),
    HourSalary: round2(h),
    HourSalaryWeekend: round2(h * (1 + ratePct(rates.WeekendRate, 50) / 100)),
    HourSalaryHoliday: round2(h * (1 + ratePct(rates.HolidayRate, 50) / 100)),
  };
}
